import { TimeRange } from '@nf/types/common';
import { openExchangeInNewWindow, useCurrentProperties, useMenuTypeList, useOriginMenuData, useSwitchParlayRoute } from '@/hooks/query';
import { useAccountInfo } from './query/account';
import { useSiteStore } from '@/store/site';
import { kenoSettings, sportLotterySettings } from '@/settings/lottery-settings';
import { UserInfo } from '@nf/types/account';
import { useGetSiteConfigs, useGetSiteConfigsWithSession } from './query/use-site-configs';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { modalService } from '@/services/modal.service';
import { sportService } from '@/services/sport.service';
import { getForbiddenSport, isNumberGames, sortVirtualSports } from '@/services/settings.service';
import { useCasinoRoyale } from '@/hooks/query/use-casino-royale';
import { useAppearanceStore } from '@/store/appearance';
import { openESport, openAdvantPlay } from '@/services/third-party-entrance.service';
import { useAuthToken } from '@/store/auth';
import { sportsbookService } from '@/services/sportsbook.service';
import { menuDefinition } from '@/types/menu-setting';
import { PromotionType } from '@nf/utils-common/constants';
import { useAtomValue } from 'jotai';
import { visitorAtom } from '@/atoms/visitor';
import { useAthenaSiteConfig } from '@/hooks/query/athena/use-athena-site-config';
import { useOddsPageSwitcher } from './use-odds-page-switcher';
import { usePredictionMarket } from '@/hooks/use-prediction-market';

export interface ProductItem {
	productName?: string;
	labelKey: string;
	path?: string;
	newUntil?: TimeRange;
	onClick?: () => void;
	subMenu?: SubItem[];
}

export interface SubItem {
	productName?: string;
	labelKey: string;
	imagePath: string;
	onClick?: () => void;
}

export const useProductList = () => {
	let productList = new Array<ProductItem>();
	const { user } = useAccountInfo();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const visitorInfo = useAtomValue(visitorAtom);
	const beforeToken = visitorInfo?.AccessToken;
	const { SiteConfigsData } = useGetSiteConfigs();
	const { SiteConfigsWithSessionData } = useGetSiteConfigsWithSession();
	const { openPredictionMarket, canSeePredictionMarket, predictionMarketNewTagUntil } = usePredictionMarket();
	const { openCasinoRoyale } = useCasinoRoyale();
	const { data: athenaSiteConfig } = useAthenaSiteConfig();
	const t = useTranslations('General');
	const { switchToParlay } = useOddsPageSwitcher();
	const deepLinkSiteInfo = useSiteStore(state => state.deepLinkSiteInfo);
	const { routePrefix: currentRoutePrefix, sport: currentSport } = useCurrentProperties();
	const view = useAppearanceStore(state => state.view);

	const { menuTypeList: parlayMenuTypeList } = useMenuTypeList(true);
	const { menuTypeList: normalMenuTypeList } = useMenuTypeList(false);	

	const parlayDefaultPathData = useSwitchParlayRoute(true);

	const { menuResponse } = useOriginMenuData({ isNeedParlayData: false });

	useEffect(() => {
		productList = generateProductList();
	}, [currentRoutePrefix, currentSport, menuResponse]);

	const generateProductList = () => {
		const list = new Array<ProductItem>();
		const order = Object.keys(menuDefinition);
		const defaultEvent =  normalMenuTypeList.find(menu => menu.modeId === 0);
		const parlayDefaultEvent = parlayMenuTypeList.find(menu => menu.modeId === 0);
		const specialEvents = normalMenuTypeList.filter(menu => menu.modeId !== 0);

		const sortedSpecialEvents = specialEvents
			?.sort((a, b) => order.indexOf(`M${a.modeId}`) - order.indexOf(`M${b.modeId}`)) ?? [];
		sortedSpecialEvents.forEach(menuType => {
			list.push({
				productName: `M${menuType.modeId}`,
				labelKey: sportsbookService.makeMenuNodeResourceKey(SiteConfigsData?.EnableFilterSaba, menuType.modeId.toString()),
				path: menuType.path,
			});
		});
		/*var name=['lbl_AllLive','lbl_Sports','lbl_mixparlay','lbl_SoccerClub','#43','lbl_SabaPinG','lbl_VirtualSport','#161'];*/
	
		const virtualSportsMenu = menuResponse.filter(menu => sportService.isVirtualSports(menu.GameId));
		const NumberGamesMenu = menuResponse.filter(menu => isNumberGames(menu.GameId));	
	
		if (defaultEvent) {
			list.push({
				productName: 'sports',
				labelKey: 'lbl_Sports',
				path: defaultEvent.path
			});
		}

		if (parlayDefaultEvent) {	
			list.push({
				productName: 'parlay',
				labelKey: 'lbl_mixparlay',
				path: `/sports/${parlayDefaultPathData.sport}/${parlayDefaultPathData.market}`,
				onClick: () => switchToParlay()
			});
		}
		if (user?.ActStatus === 2 && SiteConfigsData?.EnableMyFavorite) {
			list.push({
				productName: 'favorite',
				labelKey: 'lbl_favorite',
				path: '/favorites'
			});

		}
		if ((!user && SiteConfigsWithSessionData?.EnableExchangeForBefore) || user?.Exchange) {
			list.push({
				productName: 'exchange',
				labelKey: 'lbl_Exchange',
				onClick: () => openExchangeInNewWindow(isBefore, user, SiteConfigsData, t('lbl_deposit'))
			});
		}
		if (user?.ActStatus === 2 && user?.EnableESportWeb && SiteConfigsData) {
			const esportsData = {
				siteId: SiteConfigsData.GetMessageID,
				umSource: t('lbl_GameUnderMaintain'),
				isApp: deepLinkSiteInfo.isApp
			};
			list.push({
				productName: 'eSports',
				labelKey: 'lbl_ESports',
				onClick: () => openESport(esportsData, isBefore? beforeToken: authToken, SiteConfigsData?.EnableEsportsDeeplink ?? false)
			});
		}
	
		// if (user?.CanSeeAllPinGoal) {
		// 	list.push({
		// 		labelKey: 'lbl_SabaPinG',
		// 		onClick: openSabaPingGoal
		// 	});
		// }
		const forbiddenSports = getForbiddenSport(view);

		const enableVirtualSport = virtualSportsMenu.filter(menu => {
			return !forbiddenSports.includes(menu.GameId.toString());
		})

		const enableBetbyVirtualSports = [
			...(user?.LicenseeSiteSetting?.EnableBetbyEsoccer ? [{ GameId: 116 }] : []),
			...(user?.LicenseeSiteSetting?.EnableBetbyBasketball2K ? [{ GameId: 134 }] : []),
		].flat();

		const allEnableVirtualSports = [...enableVirtualSport, ...enableBetbyVirtualSports];
		
		const sortedEnableVirtualSport = sortVirtualSports(allEnableVirtualSports);

		if (user?.ActStatus === 2 && sortedEnableVirtualSport.length) {
			list.push({
				productName: 'virtualSports',
				labelKey: 'lbl_VirtualSport',
				path: sortedEnableVirtualSport && sortedEnableVirtualSport.length > 0 && [134, 116].includes(sortedEnableVirtualSport[0].GameId)
					? `/sports/${sortedEnableVirtualSport[0].GameId}`
					: `/sports/${sortedEnableVirtualSport[0].GameId}/t`,
			});
		}
	
		const enableNumberGame = NumberGamesMenu.filter(menu => {
			return !forbiddenSports.includes(menu.GameId.toString());
		})

		if (user?.ActStatus === 2 && enableNumberGame.length) {
			list.push({
				productName: 'numberGame',
				labelKey: 'lbl_161',
				path: `/sports/${enableNumberGame[0].GameId}/t`
			});
		}
	
		if (user?.VGamingMobi) {
			list.push({
				productName: 'vGaming',
				labelKey: 'lbl_VGamingMobiL',
				onClick: () => {
					if (user.ActStatus != 2) {
						modalService.openMessageModal(t('lbl_deposit'));
					} else {
						openCasinoRoyale({ sportTypeId: '171', betTypeId: '0', isApp: deepLinkSiteInfo.isApp });
					}
				}
			});
		}
	
		const kenoProducts: ProductItem = {
			productName: 'kenoLotteryMain',
			labelKey: kenoSettings.labelKey,
			subMenu: []
		};
	
		kenoSettings.subItems.forEach(kenoItems => {
			let permissionKey: number = 0;
			
			if (kenoItems.permissionKey === 'GPILottery') {
				permissionKey = Number(athenaSiteConfig?.CanSeeGPILottery ?? 0);
			} else {
				permissionKey = Number(user?.[kenoItems.permissionKey as keyof UserInfo] ?? 0);
			}
			
			if (permissionKey) {
				kenoProducts.subMenu?.push({
					productName: kenoItems.productName,
					labelKey: kenoItems.labelKey,
					imagePath: kenoItems.imagePath,
					onClick: () => {
						const params = kenoItems.route.split(',');
						openCasinoRoyale({ sportTypeId: params[0], betTypeId: params[1], isApp: deepLinkSiteInfo.isApp });
					}
				});
			}
		});
	
		if (kenoProducts.subMenu?.length) {
			list.push(kenoProducts);
		}
	
		const sportLotteryProducts: ProductItem = {
			productName: 'sportLotteryMain',
			labelKey: sportLotterySettings.labelKey,
			subMenu: []
		};
	
		sportLotterySettings.subItems.forEach(sportLotteryItems => {
			const permissionKey = user?.[sportLotteryItems.permissionKey as keyof UserInfo] ?? 0; //MaxGame
	
			if (permissionKey) {
				sportLotteryProducts.subMenu?.push({
					productName: sportLotteryItems.productName,
					labelKey: sportLotteryItems.labelKey,
					imagePath: sportLotteryItems.imagePath,
					onClick: () => {
						const params = sportLotteryItems.route.split(',');
						openCasinoRoyale({ sportTypeId: params[0], betTypeId: params[1], isApp: deepLinkSiteInfo.isApp, platform: 'd' });
					}
				});
			}
		});
	
		if (sportLotteryProducts.subMenu?.length) {
			list.push(sportLotteryProducts);
		}
	
		if (user?.CanSeeAdvantPlayVendorLobby || SiteConfigsData?.CanSeeAdvantPlayVendorLobby) {
			list.push({
				productName: 'casinoRoyale',
				labelKey: 'lbl_Advantplay',
				onClick: () => {
					if (user?.ActStatus != 2) {
						modalService.openMessageModal(t('lbl_deposit'));
					} else {
						openAdvantPlay({ isApp: deepLinkSiteInfo.isApp }, authToken ?? undefined);
					}
				}
			});
		}

		if (SiteConfigsData?.EnableStreamerSchedule) {
			list.unshift({
				productName: 'streamer',
				labelKey: 'lbl_Streamer',
				path: '/streamer',
			});
		}

		if (SiteConfigsData?.EnablePromotion ?? 0 > PromotionType.Close) {
			list.unshift({
				productName: 'promotion',
				labelKey: 'lbl_ECPPromotion',
				path: '/promotion',
			});
		}

		if (canSeePredictionMarket) {
			list.push({
				labelKey: 'lbl_PredictMarket',
				newUntil: { to: predictionMarketNewTagUntil },
				onClick: openPredictionMarket
			});
		}

		return list;
	}

	productList = generateProductList();

	return productList;
};

export const use3rdPartyProductList = () => {
	const { user } = useAccountInfo();
	const view = useAppearanceStore(state => state.view);
	const forbiddenSport = getForbiddenSport(view);
	const thirdPartyGameList = [
		{ 
			name: 'lbl_161', 
			productName: 'numberGame',
			icon: '161', 
			href: '/sports/161/l', 
			hasLive: true, 
			enable: !forbiddenSport.includes('161') && user?.NG  
		},
		{ 
			name: 'lbl_164', 
			productName: 'happy5',
			icon: '164', 
			href: '/sports/164/l', 
			hasLive: true, 
			enable: !forbiddenSport.includes('164') && user?.Happy5  
		},
		{ 
			name: 'lbl_Keno', 
			productName: 'keno',
			icon: '202', 
			href: '/game-lobby/?sid=202&bid=0', 
			enable: user?.Keno 
		},
		{ 
			name: 'lbl_KenoLottery',
			productName: 'kenoLottery', 
			icon: '220', 
			href: '/game-lobby/?sid=220&bid=0', 
			enable: user?.KL 
		},
		{ 
			name: 'lbl_VGaming', 
			productName: 'vGaming',
			icon: '171', 
			href: '/game-lobby/?sid=171&bid=0', 
			enable: user?.VGaming && (user?.ActStatus == 2) 
		},
		{ 
			name: 'lbl_Arcadia', 
			productName: 'arcadia',
			icon: '165', 
			href: '/game-lobby/?sid=165',  
			enable: user?.MiniStatus 
		},
		{ 
			name: 'lbl_LiveCasino', 
			productName: 'liveCasino',
			icon: 'game-lobby', 
			href: '/game-lobby/?cid=2',  
			enable: user?.LCas 
		},
	];
	return { thirdPartyGameList };
};